import { v4 as uuid } from 'uuid';
import { deleteOwnedMedia, uploadOwnedMedia } from '../../util/ownedMedia';

const ENTITY_TYPE = 'CREATIVE_PROJECT';

export const PROJECT_IMAGE_ACCEPT = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.heic',
  '.heif',
].join(',');

export const PROJECT_VIDEO_ACCEPT = [
  'video/mp4',
  'video/quicktime',
  'video/webm',
  '.mp4',
  '.mov',
  '.webm',
].join(',');

export const PROJECT_AUDIO_ACCEPT = [
  'audio/mpeg',
  'audio/mp3',
  'audio/mp4',
  'audio/aac',
  'audio/wav',
  'audio/x-wav',
  'audio/webm',
  'audio/ogg',
  '.mp3',
  '.m4a',
  '.aac',
  '.wav',
  '.webm',
  '.ogg',
].join(',');

export const PROJECT_AUDIO_COVER_ACCEPT = [
  'image/jpeg',
  'image/png',
  'image/webp',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
].join(',');

const ALLOWED_PROJECT_IMAGE_MIMES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
]);

const ALLOWED_PROJECT_THUMBNAIL_MIMES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

const ALLOWED_PROJECT_VIDEO_MIMES = new Set([
  'video/mp4',
  'video/quicktime',
  'video/webm',
]);

const ALLOWED_PROJECT_AUDIO_MIMES = new Set([
  'audio/mpeg',
  'audio/mp3',
  'audio/mp4',
  'audio/aac',
  'audio/wav',
  'audio/x-wav',
  'audio/webm',
  'audio/ogg',
]);

const MEDIA_KIND_MIME_ALLOWLIST = {
  IMAGE: ALLOWED_PROJECT_IMAGE_MIMES,
  THUMBNAIL: ALLOWED_PROJECT_THUMBNAIL_MIMES,
  AUDIO_COVER: ALLOWED_PROJECT_THUMBNAIL_MIMES,
  VIDEO: ALLOWED_PROJECT_VIDEO_MIMES,
  AUDIO: ALLOWED_PROJECT_AUDIO_MIMES,
};

const MEDIA_KIND_EXTENSION_CONTENT_TYPE = {
  IMAGE: {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    heic: 'image/heic',
    heif: 'image/heif',
  },
  THUMBNAIL: {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
  },
  AUDIO_COVER: {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
  },
  VIDEO: {
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    webm: 'video/webm',
  },
  AUDIO: {
    mp3: 'audio/mpeg',
    m4a: 'audio/mp4',
    aac: 'audio/aac',
    wav: 'audio/wav',
    webm: 'audio/webm',
    ogg: 'audio/ogg',
  },
};

const CONTENT_TYPE_ALIASES = {
  'image/jpg': 'image/jpeg',
  'image/pjpeg': 'image/jpeg',
  'image/x-png': 'image/png',
};

function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim() !== '';
}

function isBlobUrl(value) {
  return typeof value === 'string' && value.startsWith('blob:');
}

function isExternalUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value);
}

function getFileMime(file) {
  const mime = (file?.type || '').toLowerCase().trim();
  return CONTENT_TYPE_ALIASES[mime] || mime;
}

function getFileExtension(fileOrName) {
  const name = typeof fileOrName === 'string' ? fileOrName : fileOrName?.name;
  const match = /\.([^.?#/]+)(?:[?#].*)?$/i.exec(name || '');
  return match?.[1]?.toLowerCase() || '';
}

export function normalizeProjectMediaContentType(file, mediaKind) {
  const mime = getFileMime(file);
  const allowedMimes = MEDIA_KIND_MIME_ALLOWLIST[mediaKind];
  const extensionContentTypes = MEDIA_KIND_EXTENSION_CONTENT_TYPE[mediaKind];

  if (!allowedMimes || !extensionContentTypes) {
    return '';
  }

  if (mime) {
    if (allowedMimes.has(mime)) {
      return mime;
    }

    if (mime !== 'application/octet-stream') {
      return '';
    }
  }

  return extensionContentTypes[getFileExtension(file)] || '';
}

export function isAllowedProjectImageFile(file) {
  return Boolean(normalizeProjectMediaContentType(file, 'IMAGE'));
}

export function isAllowedProjectVideoFile(file) {
  return Boolean(normalizeProjectMediaContentType(file, 'VIDEO'));
}

export function isAllowedProjectAudioFile(file) {
  return Boolean(normalizeProjectMediaContentType(file, 'AUDIO'));
}

export function isAllowedProjectAudioCoverFile(file) {
  return Boolean(normalizeProjectMediaContentType(file, 'AUDIO_COVER'));
}

function isVideoMedia(item) {
  if (item?.file) {
    return isAllowedProjectVideoFile(item.file);
  }

  const mime = getFileMime(item);
  const url = typeof item === 'string' ? item : item?.url;
  return ALLOWED_PROJECT_VIDEO_MIMES.has(mime) || Boolean(MEDIA_KIND_EXTENSION_CONTENT_TYPE.VIDEO[getFileExtension(url)]);
}

function getExistingImageUrl(image) {
  if (typeof image === 'string') return image;
  return image?.url || image?.processedUrl || image?.originalUrl || null;
}

function getAudioUrl(audio) {
  return audio?.url || audio?.audioKey || audio?.originalUrl || null;
}

function getFileName(file, fallback) {
  return file?.name || fallback;
}

function getFetch(fetchImpl) {
  const resolvedFetch = fetchImpl || (typeof fetch === 'function' ? fetch : undefined);
  if (typeof resolvedFetch !== 'function') {
    throw new Error('fetch ist fuer das Video-Vorschaubild nicht verfuegbar.');
  }
  return resolvedFetch;
}

async function fileFromBlobUrl(blobUrl, fileName, fetchImpl) {
  const response = await getFetch(fetchImpl)(blobUrl);
  const blob = await response.blob();
  const contentType = blob.type || 'image/jpeg';

  if (typeof File !== 'undefined') {
    return new File([blob], fileName, { type: contentType });
  }

  blob.name = fileName;
  return blob;
}

function addUploadedMedia(uploadedMedia, media) {
  uploadedMedia.push(media);
  return media.key;
}

async function uploadProjectMedia({
  file,
  creativeId,
  projectId,
  mediaKind,
  slot,
  fileName,
  uploadMedia,
  uploadedMedia,
}) {
  const contentType = normalizeProjectMediaContentType(file, mediaKind);
  if (!contentType) {
    throw new Error('Medienformat ist nicht erlaubt.');
  }

  const uploadResult = await uploadMedia({
    file,
    entityType: ENTITY_TYPE,
    entityId: projectId,
    parentEntityId: creativeId,
    mediaKind,
    slot,
    fileName,
    contentType,
  });

  return addUploadedMedia(uploadedMedia, {
    key: uploadResult.key,
    entityType: ENTITY_TYPE,
    entityId: projectId,
    parentEntityId: creativeId,
    mediaKind,
  });
}

export async function cleanupCreativeProjectUploadedMedia({
  uploadedMedia,
  deleteMedia = deleteOwnedMedia,
}) {
  const uniqueMedia = [];
  const seen = new Set();

  for (const media of uploadedMedia || []) {
    const marker = [
      media?.key,
      media?.entityType,
      media?.entityId,
      media?.parentEntityId || '',
      media?.mediaKind,
    ].join('|');

    if (!media?.key || seen.has(marker)) continue;
    seen.add(marker);
    uniqueMedia.push(media);
  }

  if (!uniqueMedia.length) {
    return null;
  }

  const cleanupResults = await Promise.allSettled(
    uniqueMedia.map((media) => deleteMedia(media))
  );

  return cleanupResults.find((result) => result.status === 'rejected')?.reason || null;
}

export function resolveCreativeProjectId({
  isEditing,
  projectId,
  project,
  idGenerator = uuid,
}) {
  if (isEditing) {
    return project?.id || projectId;
  }

  return idGenerator();
}

export async function prepareCreativeProjectImages({
  creativeId,
  projectId,
  images,
  uploadMedia = uploadOwnedMedia,
  uploadedMedia = [],
  fetchImpl,
}) {
  if (!creativeId) {
    throw new Error('Creative-Profil konnte nicht zugeordnet werden.');
  }
  if (!projectId) {
    throw new Error('Projekt konnte nicht zugeordnet werden.');
  }

  const projectImages = [];

  for (const [index, image] of (Array.isArray(images) ? images : []).entries()) {
    const imageData = { order: index };

    if (image?.file) {
      if (image.type === 'video' && !isAllowedProjectVideoFile(image.file)) {
        throw new Error('Videoformat ist nicht erlaubt. Bitte nutze MP4, MOV oder WEBM.');
      }
      if (image.type !== 'video' && !isAllowedProjectImageFile(image.file)) {
        throw new Error('Bildformat ist nicht erlaubt. Bitte nutze JPG, PNG, WEBP, HEIC oder HEIF.');
      }

      const isVideo = isVideoMedia(image);
      const mediaKind = isVideo ? 'VIDEO' : 'IMAGE';
      const slot = isVideo ? 'video' : 'image';
      imageData.url = await uploadProjectMedia({
        file: image.file,
        creativeId,
        projectId,
        mediaKind,
        slot,
        fileName: getFileName(image.file, isVideo ? 'project-video.mp4' : 'project-image.jpg'),
        uploadMedia,
        uploadedMedia,
      });

      if (isNonEmptyString(image.caption)) {
        imageData.caption = image.caption;
      }

      if (isNonEmptyString(image.thumbnailUrl)) {
        if (isBlobUrl(image.thumbnailUrl)) {
          const thumbnailFile = await fileFromBlobUrl(
            image.thumbnailUrl,
            `video-thumbnail-${uuid()}.jpg`,
            fetchImpl
          );
          imageData.thumbnailUrl = await uploadProjectMedia({
            file: thumbnailFile,
            creativeId,
            projectId,
            mediaKind: 'THUMBNAIL',
            slot: 'video-thumbnail',
            fileName: getFileName(thumbnailFile, 'video-thumbnail.jpg'),
            uploadMedia,
            uploadedMedia,
          });
        } else {
          imageData.thumbnailUrl = image.thumbnailUrl;
        }
      }

      projectImages.push(imageData);
      continue;
    }

    const existingUrl = getExistingImageUrl(image);
    if (!existingUrl) {
      continue;
    }

    imageData.url = existingUrl;
    if (isNonEmptyString(image?.caption)) {
      imageData.caption = image.caption;
    }
    if (isNonEmptyString(image?.thumbnailUrl)) {
      imageData.thumbnailUrl = image.thumbnailUrl;
    }

    projectImages.push(imageData);
  }

  return projectImages;
}

function resolveAudioArtists(audio, formArtists) {
  if (Array.isArray(audio?.artists) && audio.artists.length > 0) {
    const filteredArtists = audio.artists.filter((artist) => artist && artist.trim() !== '');
    if (filteredArtists.length > 0) {
      return filteredArtists;
    }
  }

  if (typeof audio?.artists === 'string' && audio.artists.trim()) {
    return audio.artists.split(',').map((artist) => artist.trim()).filter(Boolean);
  }

  return Array.isArray(formArtists) ? formArtists : [];
}

function resolveExistingCoverImage(coverImage) {
  if (typeof coverImage === 'string') {
    return coverImage;
  }
  if (isExternalUrl(coverImage?.url)) {
    return coverImage.url;
  }
  if (isNonEmptyString(coverImage?.key)) {
    return coverImage.key;
  }
  return null;
}

export async function prepareCreativeProjectAudios({
  creativeId,
  projectId,
  audios,
  formArtists,
  formGenre,
  uploadMedia = uploadOwnedMedia,
  uploadedMedia = [],
}) {
  if (!creativeId) {
    throw new Error('Creative-Profil konnte nicht zugeordnet werden.');
  }
  if (!projectId) {
    throw new Error('Projekt konnte nicht zugeordnet werden.');
  }

  const projectAudios = [];

  for (const [index, audio] of (Array.isArray(audios) ? audios : []).entries()) {
    const audioData = {
      title: audio?.title,
      artists: resolveAudioArtists(audio, formArtists),
      genre: audio?.genre || formGenre || '',
      order: audio?.order ?? index,
      durationMs: audio?.durationMs || 0,
    };

    if (audio?.file) {
      if (!isAllowedProjectAudioFile(audio.file)) {
        throw new Error('Audioformat ist nicht erlaubt. Bitte nutze MP3, M4A, AAC, WAV, WEBM oder OGG.');
      }

      audioData.url = await uploadProjectMedia({
        file: audio.file,
        creativeId,
        projectId,
        mediaKind: 'AUDIO',
        slot: 'audio',
        fileName: getFileName(audio.file, 'project-audio.mp3'),
        uploadMedia,
        uploadedMedia,
      });
    } else {
      const audioUrl = getAudioUrl(audio);
      if (!audioUrl) {
        throw new Error(`Fehler: Audiodatei "${audio?.title || 'Audio'}" konnte nicht zugeordnet werden.`);
      }
      audioData.url = audioUrl;
    }

    if (audio?.coverImage?.file) {
      if (!isAllowedProjectAudioCoverFile(audio.coverImage.file)) {
        throw new Error('Cover-Bildformat ist nicht erlaubt. Bitte nutze JPG, PNG oder WEBP.');
      }

      audioData.coverImage = await uploadProjectMedia({
        file: audio.coverImage.file,
        creativeId,
        projectId,
        mediaKind: 'AUDIO_COVER',
        slot: 'audio-cover',
        fileName: getFileName(audio.coverImage.file, 'audio-cover.jpg'),
        uploadMedia,
        uploadedMedia,
      });
    } else {
      const coverImage = resolveExistingCoverImage(audio?.coverImage);
      if (!coverImage) {
        throw new Error(`Fehler: Audiodatei "${audio?.title || 'Audio'}" hat kein Cover-Bild.`);
      }
      audioData.coverImage = coverImage;
    }

    projectAudios.push(audioData);
  }

  return projectAudios;
}

export async function prepareCreativeProjectMedia({
  creativeId,
  projectId,
  images,
  audios,
  formArtists,
  formGenre,
  uploadMedia = uploadOwnedMedia,
  uploadedMedia = [],
  fetchImpl,
}) {
  const preparedImages = await prepareCreativeProjectImages({
    creativeId,
    projectId,
    images,
    uploadMedia,
    uploadedMedia,
    fetchImpl,
  });
  const preparedAudios = await prepareCreativeProjectAudios({
    creativeId,
    projectId,
    audios,
    formArtists,
    formGenre,
    uploadMedia,
    uploadedMedia,
  });

  return {
    images: preparedImages,
    audios: preparedAudios,
  };
}

export async function saveCreativeProjectWithOwnedMedia({
  creative,
  isEditing,
  projectId,
  project,
  images,
  audios,
  formArtists,
  formGenre,
  buildProjectData,
  handleCreateProject,
  handleUpdateProject,
  uploadMedia = uploadOwnedMedia,
  deleteMedia = deleteOwnedMedia,
  idGenerator = uuid,
  fetchImpl,
}) {
  const resolvedProjectId = resolveCreativeProjectId({
    isEditing,
    projectId,
    project,
    idGenerator,
  });
  const uploadedMedia = [];

  try {
    const preparedMedia = await prepareCreativeProjectMedia({
      creativeId: creative?.id,
      projectId: resolvedProjectId,
      images,
      audios,
      formArtists,
      formGenre,
      uploadMedia,
      uploadedMedia,
      fetchImpl,
    });

    const projectData = buildProjectData({
      projectId: resolvedProjectId,
      images: preparedMedia.images,
      audios: preparedMedia.audios,
    });

    return isEditing
      ? await handleUpdateProject(projectData)
      : await handleCreateProject(projectData);
  } catch (error) {
    const cleanupError = await cleanupCreativeProjectUploadedMedia({
      uploadedMedia,
      deleteMedia,
    });

    if (cleanupError) {
      error.cleanupError = cleanupError;
    }

    throw error;
  }
}
